import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, X } from 'lucide-react';
import { ProjectType } from '../types';
import { localized } from '../utils';
import { BACKEND_URL } from '../config';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (type: ProjectType) => void;
  onDeleted: (id: number) => void;
}

export default function ProjectTypeManagerModal({ open, onClose, onCreated, onDeleted }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [allTypes, setAllTypes] = useState<ProjectType[]>([]);
  const [nameFr, setNameFr] = useState('');
  const [nameEn, setNameEn] = useState('');

  useEffect(() => {
    if (!open) return;
    fetch(`${BACKEND_URL}/api/project-types`).then(r => r.json()).then(setAllTypes).catch(console.error);
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const handleAdd = async () => {
    if (!nameFr || !nameEn) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/project-types`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: { fr: nameFr, en: nameEn } }),
      });
      if (res.ok) {
        const created = await res.json();
        setAllTypes(prev => [...prev, created]);
        onCreated(created);
        setNameFr('');
        setNameEn('');
      }
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id: number) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/project-types/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setAllTypes(prev => prev.filter(t => t.id !== id));
      onDeleted(id);
    } catch (err) { console.error(err); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="glass rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-zinc-100">{t('project_types')}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button>
        </div>

        <div className="flex flex-col gap-2 mb-4 p-3 bg-ink/[0.04] rounded-lg">
          <div className="flex gap-2">
            <input className="glass rounded-lg flex-1 border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3 py-2 text-sm" placeholder={t('name_fr') as string} value={nameFr} onChange={e => setNameFr(e.target.value)} />
            <input className="glass rounded-lg flex-1 border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3 py-2 text-sm" placeholder={t('name_en') as string} value={nameEn} onChange={e => setNameEn(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }} />
          </div>
          <button onClick={handleAdd} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark text-sm flex items-center justify-center gap-1 transition-colors">
            <Plus size={16} /> {t('add_project_type')}
          </button>
        </div>

        {allTypes.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center">{t('no_project_types')}</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {allTypes.sort((a, b) => localized(a.name, lang).localeCompare(localized(b.name, lang), lang)).map(pt => (
              <span key={pt.id} className="flex items-center gap-1 bg-zinc-800/50 border border-ink/[0.08] px-2 py-1 rounded-lg text-xs text-zinc-300">
                <span className="flex-1">{localized(pt.name, lang)}</span>
                <span className="text-zinc-500">({pt.name[lang === 'fr' ? 'en' : 'fr'] || ''})</span>
                <button onClick={() => handleDelete(pt.id)} className="text-red-400/70 hover:text-red-400 ml-0.5"><Trash2 size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
